// footerTokens.js
//
// Bug A fix (Template Theme cascade to Footer): a pure resolver that maps a
// Template_Theme plus an optional footer override block into the concrete set of
// footer tokens (colors, font family, radius) the footer chrome should render.
//
// Precedence for every token:
//   footer-specific override  >  cascaded Template_Theme token  >  legacy default
//
// This mirrors the precedence already used by TemplateRenderer.jsx `pageStyle`
// for the `--template-footer-*` CSS variables, so the builder preview (embedded
// reader) and the published reader resolve each token identically.
//
// All inputs are optional. null / undefined / empty-string are treated as
// "no override" so the theme token cascades through. This module is pure and
// has no side effects.

/**
 * Returns the first argument that is a "present" value, treating
 * null / undefined / empty-or-whitespace string as absent so the next
 * fallback (theme token, then legacy default) wins.
 *
 * @param {...*} values - candidate values in precedence order
 * @returns {*} the first present value, or undefined when none are present
 */
const firstPresent = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return undefined;
};

/**
 * True when the footer's background should resolve to the literal 'transparent'.
 * Supports both the normalized `background.transparent === true` shape and the
 * legacy `backgroundColor === 'transparent'` string.
 *
 * @param {object} footer
 * @returns {boolean}
 */
const isTransparentBackground = (footer) => {
  if (!footer || typeof footer !== 'object') return false;
  if (footer.background && footer.background.transparent === true) return true;
  if (typeof footer.backgroundColor === 'string'
    && footer.backgroundColor.trim().toLowerCase() === 'transparent') return true;
  return false;
};

/**
 * Resolve the concrete footer tokens for a Template, applying
 * footer-override > cascaded-theme-token > legacy-default precedence.
 *
 * @param {object} [theme] - Template_Theme tokens (attributes.theme)
 * @param {object} [footer] - footer override block (attributes.footer)
 * @returns {{
 *   backgroundColor: string,
 *   textColor: string,
 *   headingColor: string|undefined,
 *   linkColor: string|undefined,
 *   borderColor: string,
 *   fontFamily: string,
 *   radius: (string|number|undefined)
 * }}
 */
export const resolveFooterTokens = (theme, footer) => {
  const t = theme && typeof theme === 'object' ? theme : {};
  const f = footer && typeof footer === 'object' ? footer : {};
  const typography = f.typography && typeof f.typography === 'object' ? f.typography : {};
  const background = f.background && typeof f.background === 'object' ? f.background : {};

  const backgroundColor = isTransparentBackground(f)
    ? 'transparent'
    : (firstPresent(
        background.color,
        f.backgroundColor,
        t.footerBackground,
        t.background
      ) || '#ffffff');

  const textColor = firstPresent(
    typography.textColor,
    f.textColor,
    t.footerText,
    t.color
  ) || '#111827';

  const headingColor = firstPresent(
    f.headingColor,
    f.titleColor,
    t.color
  );

  const linkColor = firstPresent(
    f.linkColor,
    f.accentColor,
    t.primary
  );

  const borderColor = firstPresent(
    f.borderColor,
    t.borderColor
  ) || '#e5e7eb';

  const fontFamily = firstPresent(
    typography.fontFamily,
    f.fontFamily,
    t.fontFamily
  ) || 'inherit';

  const radius = firstPresent(
    f.radius,
    t.cardRadius
  );

  return {
    backgroundColor,
    textColor,
    headingColor,
    linkColor,
    borderColor,
    fontFamily,
    radius
  };
};

export default resolveFooterTokens;
